import axios from "axios"
import Token from './Token'
import Bowser from 'bowser'
import { useToast } from 'vue-toast-notification'
import * as AES from "@/utils/Encodepassword"
import * as Session from "@/utils/SessionStore"
import { LONG_TOAST_DURATION } from "@/utils/Utils"
import log4javascript from 'log4javascript';
// The refresh exchange, shared with the accounting front end (ARCH-17, ARCH-18).
import { createRefreshClient, REFRESH_RENEWED, REFRESH_REJECTED } from '../../../shared/refresh-client.js'

const logger = log4javascript.getLogger('ApiManager');
const toast = useToast();

const browser = Bowser.getParser(window.navigator.userAgent)
var isCheckServer = false; // this flag is use to check re-entrance to checkServerIsUpdating function
var CODE_AUTHEN = 401;
var CODE_CONNECT = 500;
var CODE_UPDATE = 502;
var arrCallApi = [];
// Vestige of the removed transport encryption: nothing sets this true any more (its only
// writer was updateKeyAES, deleted with the handshake), so the `!isUpdateKey` guards it
// gates are now always taken. Left in place to keep this change to the handshake alone.
var isUpdateKey = false;// not show toast

// Latched once the user has been told the session ended, so a burst of in-flight requests
// all failing at once yields one toast and one redirect. Stays here rather than moving into
// the shared client: how an app tells its user is app business, what "ended" means is not.
var hasExpiredSession = false;

// The last error toast shown, so several requests failing the same way say it once. See
// showErrorToast.
var lastErrorToast = { text: null, shownAt: 0 };

// The refresh exchange itself now lives in @sumtl/shared and is used by the accounting front
// end too (ARCH-17). One implementation is a correctness requirement, not tidiness — see
// ARCH-18: two clients racing a token rotation trip the server's replay detection and end the
// session for both apps.
const refreshClient = createRefreshClient({
    http: axios,
    tokens: Token,
    // Responses arrive as plain JSON; decrypt is the shim that normalises them to a string.
    decodeResponse: (data, url) => AES.decrypt(data, url),
    log: (message) => logger.debug(message),
});

const ApiManager = {
    duration: 5000,

    init() {
        axios.defaults.baseURL = this.getUrlApi();
        axios.defaults.headers = [];

        // Subscription gate: the backend returns HTTP 402 from paid-feature endpoints (add employee,
        // run payroll, submit STP, add leave) when the user has an overdue invoice. Redirect them to
        // the pay page. The invoice id comes from the X-Unpaid-Invoice-Id header (the body is
        // encrypted) which the API exposes via CORS.
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error?.response?.status === 402) {
                    const invoiceId = error.response.headers?.['x-unpaid-invoice-id'];
                    if (invoiceId) {
                        const target = `/pay-subscription-invoice/${invoiceId}`;
                        if (this.currentPath() !== target) {
                            this.navigate(target);
                        }
                    }
                }
                return Promise.reject(error);
            }
        );
    },

    /**
     * Leaves the SPA by handing the browser a new URL.
     *
     * A method rather than an inline `window.location` assignment so the two places that
     * redirect (the 402 subscription gate and expireSession) can be tested: jsdom refuses
     * to navigate, so an inline assignment is both unobservable and noisy in test output.
     *
     * @param {string} path - Where to send the browser.
     */
    navigate(path) {
        window.location.href = path;
    },

    /**
     * The path the browser is currently on. Paired with navigate() — both redirects check
     * it first to avoid bouncing a page to itself.
     *
     * @returns {string} The current pathname.
     */
    currentPath() {
        return window.location.pathname;
    },

    /** returns the domain of the server serving UI */
    ipServerUI() {
        var domain = window.APP_CONFIG?.VUE_APP_IP_SERVER_UI;
        if (!domain || (typeof domain === 'string' && domain.startsWith('__'))) {
            domain = process.env.VUE_APP_IP_SERVER_UI;
        }
        return domain
    },

    /** returns the url of the api */
    getUrlApi() {
        var url = window.APP_CONFIG?.VUE_APP_API_URL;
        if (!url || (typeof url === 'string' && url.startsWith('__'))) {
            url = process.env.VUE_APP_API_URL;
        }
        return url;
    },

    /** return the url of the server serving the UI  */
    getUrlDomain() {
        var url = window.APP_CONFIG?.VUE_APP_DOMAIN_URL;
        if (!url || (typeof url === 'string' && url.startsWith('__'))) {
            url = process.env.VUE_APP_DOMAIN_URL;
        }
        return url;
    },

    /** returns the url of the api image */
    getUrlApiImg() {
        // Prefer an explicitly configured value (runtime APP_CONFIG, then build-time env) so an
        // environment that serves images from a different host than the API still works.
        var url = window.APP_CONFIG?.VUE_APP_API_IMG;
        if (!url || (typeof url === 'string' && url.startsWith('__'))) {
            url = process.env.VUE_APP_API_IMG;
        }
        // Still nothing usable (e.g. VUE_APP_API_IMG was never set on this deployment, so the
        // placeholder got blanked and the build-time fallback is a dev value)? Derive the image
        // host from the API URL, which is reliably env-correct. Avoids leaking the dev
        // sumtl.localhost host onto remote servers.
        if (!url || (typeof url === 'string' && url.startsWith('__'))) {
            try {
                url = `${new URL(this.getUrlApi()).origin}/assets/AvatarUser`;
            } catch {
                // getUrlApi() not absolute/usable — leave url as-is.
            }
        }
        return url;
    },

    /**
     * Returns the device ID based on the browser information.
     * @returns {string} The device ID.
     */
    getDeviceId() {
        return browser.getBrowserName() + '.' + browser.getBrowserVersion() + '.' + browser.getUA().replace(/\D+/g, '')
    },
    
    
    /**
     * Returns the domain URL of the current UI.
     * @returns {string} The domain URL of the current UI.
     */
    getUrlDomainUI() {
        var url = window.location.protocol + '//' + window.location.hostname;
        if (window.location.port != null && window.location.port != "") {
            url = url + ':' + window.location.port;
        }
        return url;
    },

    /**
     * Sends a GET request to the specified URL.
     * @param {string} url - The URL to send the GET request to.
     * @returns {Promise} A Promise that resolves with the decrypted response data if the request is successful, or rejects with an error if the request fails.
     */
    get(url) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })
            


            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }
    
            arrCallApi.push(json);

            axios.get(`${url}`, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                }
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                success(this.decryptResponse(response, url));
            }).catch((error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)

                const decrypted = this.decryptResponse(error.response, url);

                const logData = JSON.stringify({error, decrypted});
                logger.debug(`Error in get ${logData}`);

                this.errorHandling(error, url, false).then(() => {
                    axios.get(`${url}`, {
                        headers: {
                            'Authorization': "Bearer " + Token.getToken(),
                            'Content-Type': 'application/json; charset=utf-8'
                        }
                    }).then((response) => {
                        success(this.decryptResponse(response, url));
                    }).catch((error) => {
                        // Second failure. errorHandling only resolves when it wants the
                        // caller to retry, and it never asks twice (hasCalBack), so this
                        // always settles as a rejection.
                        this.errorHandling(error, url, true)
                            .then(() => reject(error))
                            .catch((handled) => reject(handled));
                    })
                }).catch((error) => {
                    reject(error)
                });
            });
        });
    },

    post(url, paramsBody) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })
    

            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }
    
            arrCallApi.push(json);

            axios.post(`${url}`, paramsBody, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                }
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                success(this.decryptResponse(response, url));
            }).catch((error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                
                this.errorHandling(error, url, false).then(() => {
                    axios.post(`${url}`, paramsBody, {
                        headers: {
                            'Authorization': "Bearer " + Token.getToken(),
                            'Content-Type': 'application/json; charset=utf-8'
                        }
                    }).then((response) => {
                        success(this.decryptResponse(response, url));
                    }).catch((error) => {
                        // Second failure. errorHandling only resolves when it wants the
                        // caller to retry, and it never asks twice (hasCalBack), so this
                        // always settles as a rejection.
                        this.errorHandling(error, url, true)
                            .then(() => reject(error))
                            .catch((handled) => reject(handled));
                    })
                }).catch((error) => {
                    reject(error)
                });
                
            });
        });
    },

    put(url, paramsBody) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })


            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }
            
            arrCallApi.push(json)

            axios.put(`${url}`, paramsBody, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                }
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                success(this.decryptResponse(response, url));
            }).catch((error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                this.errorHandling(error, url, false).then(() => {
                    axios.put(`${url}`, paramsBody, {
                        headers: {
                            'Authorization': "Bearer " + Token.getToken(),
                            'Content-Type': 'application/json; charset=utf-8'
                        }
                    }).then((response) => {
                        success(this.decryptResponse(response, url));
                    }).catch((error) => {
                        // Second failure. errorHandling only resolves when it wants the
                        // caller to retry, and it never asks twice (hasCalBack), so this
                        // always settles as a rejection.
                        this.errorHandling(error, url, true)
                            .then(() => reject(error))
                            .catch((handled) => reject(handled));
                    })
                }).catch((error) => {
                    reject(error)
                });
            });
        });
    },

    delete(url) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })


            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }

            arrCallApi.push(json)

            axios.delete(`${url}`, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                }
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                success(this.decryptResponse(response, url));
            }).catch((error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                this.errorHandling(error, url, false).then(() => {
                    axios.delete(`${url}`, {
                        headers: {
                            'Authorization': "Bearer " + Token.getToken(),
                            'Content-Type': 'application/json; charset=utf-8'
                        }
                    }).then((response) => {
                        success(this.decryptResponse(response, url));
                    }).catch((error) => {
                        // Second failure. errorHandling only resolves when it wants the
                        // caller to retry, and it never asks twice (hasCalBack), so this
                        // always settles as a rejection.
                        this.errorHandling(error, url, true)
                            .then(() => reject(error))
                            .catch((handled) => reject(handled));
                    })
                }).catch((error) => {
                    reject(error)
                });
            });
        });
    },

    sendFile(url, paramsBody) {
        return new Promise((success, reject) => {


            axios.post(`${url}`, paramsBody, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'multipart/form-data'
                }
            }).then((response) => {
                success(response.data);
            }).catch((error) => {
                var errorData;
                if (error.response != undefined && error.response.data != undefined && error.response.data != "") {
                    errorData = error.response.data;
                } else {
                    errorData = error;
                }
                if (!isUpdateKey) {
                    const errorText = this.getErrorText(errorData);
                    this.showErrorToast(errorText);
                }
                reject(errorData);
            });
        });
    },

    /**
     * Downloads a file from the specified URL.
     *
     * Contract for file-download endpoints (e.g. ABAFileForPayroll): the server returns a binary
     * File only on the 2xx success path; on error it returns 400 + JSON problem-details
     * ({ detail, errors }) like the rest of the API. Because this request uses
     * responseType: 'blob', axios wraps that error JSON in a Blob too — so the catch handler must
     * read it (await blob.text() -> JSON.parse) before getErrorText can surface `detail`.
     * Success vs error is separated by HTTP status: 2xx -> .then (real file blob),
     * 4xx -> .catch (JSON-as-blob).
     *
     * @param {string} url - The URL of the file to download.
     * @returns {Promise<Blob>} A promise that resolves with the downloaded file as a Blob object.
     */
    downloadFileABA(url) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })
            

            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }

            arrCallApi.push(json)

            axios.get(`${url}`, {
                headers: { 
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                },
                responseType: 'blob'
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                let blobFile = new Blob([response.data], { type: response.headers['content-type'] });
                success(blobFile)
            }).catch(async (error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                var errorData;
                // With responseType 'blob' the error body arrives as a Blob, so read and
                // parse it to expose the JSON error (e.g. { detail: "Error - Company BSB..." }).
                if (error.response?.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        errorData = text ? JSON.parse(text) : error;
                    } catch {
                        errorData = error;
                    }
                } else if (error.response != undefined && error.response.data != undefined && error.response.data != "") {
                    errorData = error.response.data;
                } else {
                    errorData = error;
                }
                if(!isUpdateKey) {
                    const errorText = this.getErrorText(errorData);
                    this.showErrorToast(errorText);
                }
                reject(errorData);
            });
        });
    },

    /**
     * Downloads the superannuation (SAFF) file for a payroll.
     *
     * Same blob-download contract as downloadFileABA: the server returns a binary File on 2xx and
     * 400 + JSON problem-details ({ detail, errors }) on error. Because responseType is 'blob', the
     * error JSON arrives wrapped in a Blob, so the catch handler reads it (await blob.text() ->
     * JSON.parse) before getErrorText can surface `detail`.
     *
     * @param {string} url - The URL of the file to download.
     * @returns {Promise<Blob>} A promise that resolves with the downloaded file as a Blob object.
     */
    downloadFileSuper(url) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })


            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }

            arrCallApi.push(json)

            axios.get(`${url}`, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                },
                responseType: 'blob'
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                let blobFile = new Blob([response.data], { type: response.headers['content-type'] });
                success(blobFile)
            }).catch(async (error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                var errorData;
                // With responseType 'blob' the error body arrives as a Blob, so read and
                // parse it to expose the JSON error (e.g. { detail: "Error - Company ABN..." }).
                if (error.response?.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        errorData = text ? JSON.parse(text) : error;
                    } catch {
                        errorData = error;
                    }
                } else if (error.response != undefined && error.response.data != undefined && error.response.data != "") {
                    errorData = error.response.data;
                } else {
                    errorData = error;
                }
                if(!isUpdateKey) {
                    const errorText = this.getErrorText(errorData);
                    this.showErrorToast(errorText);
                }
                reject(errorData);
            });
        });
    },

    /**
     * Sends a POST request with JSON data to the specified URL.
     * @param {string} url - The URL to send the request to.
     * @param {Object} postBody - The JSON data to send in the request body.
     * @returns {Promise<any>} - A promise that resolves to the response data.
     * @throws {string} - Throws an error if the request is a duplicate.
     */
    async postJson(url, postBody) {

        const json = AES.encrypt({
            url: url,
            path: window.location.pathname
        })
        

        if (arrCallApi.indexOf(json) >= 0) {
            throw new Error("Duplicate request");
        }

        arrCallApi.push(json)

        try {
            const httpOptions = {
                headers: { 
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                }
            };

            const response = await axios.post(url, postBody, httpOptions);

            const decryptedData = AES.decrypt(response.data, url);

            const responseContentType = response.headers['content-type'];


            if (response.status === 200) {
                if (responseContentType && responseContentType.includes('application/json')){
                    return JSON.parse(decryptedData);
                }
                return response.data;
            }

            if (response.status === 204)
                return; // no data


            //
            // handle cases of errors
            //
            if (responseContentType) {
                if (responseContentType.includes('application/problem+json') || responseContentType.includes('application/json')){
                    const problem = JSON.parse(decryptedData);
                    throw problem;
                }
            }

            throw new Error(response.statusText);
        }
        finally {
            arrCallApi.splice(arrCallApi.indexOf(json), 1)
        }

    },

    /**
     * Sends a POST request with JSON data to the specified URL.
     * @param {string} url - The URL to send the request to.
     * @param {Object} postBody - The JSON data to send in the request body.
     * @returns {Promise<any>} - A promise that resolves to the response data.
     * @throws {string} - Throws an error if the request is a duplicate.
     */
    async postFormData(url, postBody) {

        const json = AES.encrypt({
            url: url,
            path: window.location.pathname
        })
        

        if (arrCallApi.indexOf(json) >= 0) {
            throw new Error("Duplicate request");
        }

        arrCallApi.push(json)

        try {
            const httpOptions = {
                headers: { 
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'multipart/form-data'
                }
            };

            const response = await axios.post(url, postBody, httpOptions);

            const decryptedData = AES.decrypt(response.data, url);

            const responseContentType = response.headers['content-type'];


            if (response.status >= 200 && response.status < 300 && response.status !== 204) {
                if (responseContentType && responseContentType.includes('application/json')){
                    return JSON.parse(decryptedData);
                }
                return response.data;
            }

            if (response.status === 204)
                return; // no data


            //
            // handle cases of errors
            //
            if (responseContentType) {
                if (responseContentType.includes('application/problem+json') || responseContentType.includes('application/json')){
                    const problem = JSON.parse(decryptedData);
                    throw problem;
                }
            }

            throw new Error(response.statusText);
        }
        finally {
            arrCallApi.splice(arrCallApi.indexOf(json), 1)
        }

    },

    /**
     * Downloads a file in XML format from the specified URL.
     *
     * Same blob-download contract as downloadFileABA: the server returns a binary File on 2xx and
     * 400 + JSON problem-details on error. Because responseType is 'blob', axios wraps that error
     * JSON in a Blob too, so the catch handler reads it (await blob.text() -> JSON.parse) before
     * getErrorText can surface `detail`.
     *
     * @param {string} url - The URL of the file to download.
     * @returns {Promise<Blob>} A promise that resolves with the downloaded file as a Blob object.
     */
    downloadFileXML(url) {
        return new Promise((success, reject) => {

            var json = AES.encrypt({
                url: url,
                path: window.location.pathname
            })


            if (arrCallApi.indexOf(json) >= 0) {
                reject("Duplicate request");
            }
            
            arrCallApi.push(json)

            axios.post(`${url}`, {}, {
                headers: {
                    'Authorization': "Bearer " + Token.getToken(),
                    'Content-Type': 'application/json; charset=utf-8'
                },
                responseType: 'blob'
            }).then((response) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                let blobFile = new Blob([response.data], { type: response.headers['content-type'] });
                success(blobFile)
            }).catch(async (error) => {
                arrCallApi.splice(arrCallApi.indexOf(json), 1)
                var errorData;
                // With responseType 'blob' the error body arrives as a Blob, so read and
                // parse it to expose the JSON error (e.g. { detail: "..." }).
                if (error.response?.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        errorData = text ? JSON.parse(text) : error;
                    } catch {
                        errorData = error;
                    }
                } else if (error.response != undefined && error.response.data != undefined && error.response.data != "") {
                    errorData = error.response.data;
                } else {
                    errorData = error;
                }
                if (!isUpdateKey) {
                    const errorText = this.getErrorText(errorData);
                    this.showErrorToast(errorText);
                }
                reject(errorData);
            });
        });
    },

    /**
     * Decrypts the response data using AES encryption.
     * 
     * @param {Object} response - The response object containing the encrypted data.
     * @param {string} url - The URL used for encryption.
     * @returns {Object} - The decrypted response data.
     * @throws {string} - Throws an exception if there is an error decrypting the response.
     */
    decryptResponse(response, url) {
        // A request that never got an HTTP reply — offline, DNS failure, CORS block — has no
        // response to read. Reaching into it threw, and because both error-path callers do so
        // synchronously inside a .catch(), that throw escaped as an unhandled rejection and left
        // the caller's promise pending FOREVER: the spinner never stopped and the failure was
        // never reported. Returning null instead lets errorHandling fall through to its generic
        // "an error has occurred" path and reject properly.
        if (response === null || response === undefined) return null;

        try {
            const decryptedText = AES.decrypt(response.data, url);
            const decryptedObj = JSON.parse(decryptedText)
            return decryptedObj;
        } catch (ex) {
            logger.error(`decryptResponse failed: ${JSON.stringify(ex)}`);
            throw "Exception decrypt response";
        }
    },

    /**
     * Returns the error text based on the provided error object.
     * 
     * valid errorObject are:
     * @example
     * "error", or 
     * {detail: "error"}, or 
     * {errors: {field1: ["this field is required"], field2: ["another error"]}}
     * @returns {string} - The error text.
     */
    getErrorText(errorObject) {
        logger.debug(`getErrorText args ${JSON.stringify(errorObject)}`)

        const genericErrorText = "An error has occurred, please try again later.";

        if (!errorObject) return genericErrorText;

        if (typeof errorObject === 'string') return errorObject;

        if (errorObject?.errors) {
            const firstKey = Object.keys(errorObject.errors)[0];
            const firstFieldErrors = errorObject.errors[firstKey];
            if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
                // Capitalize the field name for better readability
                const fieldName = firstKey.charAt(0).toUpperCase() + firstKey.slice(1);
                return `${fieldName}: ${firstFieldErrors[0]}`;
            }
        }

        return errorObject?.detail ?? genericErrorText;
    },

    showErrorToast(text) {
        // One message, however many requests hit it. A screen that loads from several endpoints at
        // once — the dashboard fires four — stacked one identical toast per failure, covering the
        // widgets underneath with three copies of the same sentence. The same reasoning as the
        // 401 latch in expireSession, generalised: a burst is one event to the person reading it.
        const now = Date.now();
        if (lastErrorToast.text === text && now - lastErrorToast.shownAt < LONG_TOAST_DURATION) {
            return;
        }
        lastErrorToast = { text, shownAt: now };

        // Error toasts linger longer than this.duration (used elsewhere) and pause on hover,
        // with a visible × close affordance (see .v-toast__item--error in styles/global.css).
        toast.error(text, { position: 'top', duration: LONG_TOAST_DURATION, pauseOnHover: true });
    },

    /**
     * Handles errors that occur during API calls.
     * 
     * @param {Error} error - The error object.
     * @param {string} url - The URL of the API call.
     * @param {boolean} hasCalBack - Indicates whether the API call has a callback function.
     * @returns {Promise} - A promise that resolves with the success response or rejects with the error response.
     */
    errorHandling(error, url, hasCalBack) {

        const decrypted = this.decryptResponse(error.response, url);

        return new Promise((success, reject) => {
            if (error.response != undefined && (error.response.status === CODE_CONNECT || error.response.status === CODE_UPDATE)) {//500-502
                if (!isCheckServer) {
                    this.checkServerIsUpdating()
                        .then((response) => {
                            if (!response.isEnable) {
                                Token.destroySession();
                                reject(error);
                            } else {
                                var errorData;
                                if (error.response != undefined && error.response.data != undefined && error.response.data != "") {
                                    errorData = error.response.data;
                                } else {
                                    errorData = error;
                                }
                                if (!isUpdateKey) {
                                    const errorText = this.getErrorText(decrypted);
                                    this.showErrorToast(errorText);
                                }
                                reject(errorData);
                            }
                        }).catch(() => {
                            Token.destroySession();
                            reject(error);
                        })
                } else {
                    reject(error)
                }
            } else if (error.response != undefined && error.response.status === CODE_AUTHEN && !isUpdateKey) {//401
                // A 401 means the access token was rejected — usually because it expired.
                // Trade the refresh token for a new pair and let the caller replay the
                // request; only fall back to the login redirect when that is impossible.
                //
                // Resolving here is what triggers the retry: every caller wraps this in
                // `errorHandling(...).then(() => <re-issue the request>)`, and the retry
                // reads Token.getToken() afresh, so it picks up the new access token.
                //
                // hasCalBack is true when this IS the retry, so a still-401 response after
                // refreshing ends the session instead of looping.
                if (hasCalBack || refreshClient.isSessionDead() || !Token.getRefreshToken()) {
                    this.expireSession(error);
                    reject(error);
                } else {
                    this.refreshAuthToken().then((outcome) => {
                        if (outcome === REFRESH_RENEWED) {
                            success("token refreshed");
                            return;
                        }
                        // Unreachable server: the request fails, but the user keeps their session
                        // and the next 401 will try again.
                        if (outcome === REFRESH_REJECTED) this.expireSession(error);
                        reject(error);
                    });
                }
            } else {
                var errorData;
                if (error.response != undefined && error.response.data != undefined && error.response.data != "") {
                    try {
                        var textData = AES.decrypt(error.response.data, url);
                        errorData = JSON.parse(textData);
                        if (error.response.status == 400 && Session.has('isLogin') && Session.get('isLogin')) {
                            Token.destroySession()
                        }
                    } catch (ex) {
                        errorData = error.response.data;
                    }
                } else {
                    errorData = error;
                }
                if ((!Session.has('isSubmitSTP') || !Session.get('isSubmitSTP')) && !isUpdateKey) {
                    const errorText = this.getErrorText(errorData);
                    this.showErrorToast(errorText);
                }
                reject(errorData);
            }
        });
    },

    /**
     * Exchanges the stored refresh token for a fresh access/refresh pair.
     *
     * Single-flight: concurrent 401s share one in-flight call rather than each firing its
     * own. Never rejects — it resolves false when the session is gone — so callers have no
     * error to handle and no unobserved rejection can escape.
     *
     * Deliberately uses bare axios rather than this.put/postFormData: those route failures
     * back through errorHandling, so a 401 from the refresh call itself would recurse into
     * another refresh.
     *
     * @returns {Promise<string>} REFRESH_RENEWED, REFRESH_REJECTED (server refused the token —
     *          end the session) or REFRESH_UNAVAILABLE (could not reach the server — keep it).
     */
    refreshAuthToken() {
        return refreshClient.refresh();
    },

    /**
     * Ends the session and sends the user back to login. Guarded so a burst of 401s
     * produces one toast and one redirect rather than one per failed request.
     */
    expireSession(error) {
        // Latch on its own flag, not on isSessionDead: that one is already set by the failed
        // refresh, and the redirect below does not navigate synchronously, so neither is a
        // reliable "have we handled this yet?" signal. Without this, a page that had six
        // requests in flight showed six toasts.
        if (hasExpiredSession) return;
        hasExpiredSession = true;
        refreshClient.markSessionDead();
        Token.destroySession();
        this.showErrorToast(this.getSessionEndMessage(error));
        if (this.currentPath() !== '/login') {
            this.navigate("login");
        }
    },

    /**
     * Turns the server's X-Session-End-Reason header into something worth reading.
     *
     * Every way a session can end arrives as a bare 401, so without the header the only honest
     * message is a vague one — and "your session expired" is actively wrong when the real cause was
     * signing in somewhere else.
     *
     * @param {Object} [error] - The axios error carrying the 401 response.
     * @returns {string} Message to show the user.
     */
    getSessionEndMessage(error) {
        const reason = error?.response?.headers?.[
            'x-session-end-reason'
        ] ?? error?.response?.headers?.['X-Session-End-Reason'];

        switch (reason) {
            case 'SignedInElsewhere':
                return "You signed in on another device. This session has been logged out.";
            case 'LoggedOut':
                return "You have been logged out.";
            case 'ReplayDetected':
                // Deliberately blunt: this can mean a token was stolen and replayed.
                return "Your session was ended for security reasons. Please log in again.";
            case 'MaxLifetime':
                return "Your session has reached its time limit. Please log in again.";
            case 'Expired':
            default:
                return "Your session has expired. Please log in again.";
        }
    },

    /**
     * Retrieves the IP client from the server.
     * @returns {Promise<string>} A promise that resolves with the IP client.
     */
    getIpClient() {
        return new Promise((success, reject) => {
            var url = "/api/Auth/IpClient"
            axios.get(`${url}`).then((response) => {
                var jsonData = JSON.parse(AES.decrypt(response.data, url))
                var ipClient = jsonData.listIpClient.remoteIpAddress.replace('::1', '127.0.0.1');
                Session.set('cache_ipClient', ipClient)
                if (!Session.has('hasGetLocation') || !Session.get('hasGetLocation')) {
                    Session.set('cache_address', jsonData.city.city + ", " + jsonData.city.country)
                }
                success(ipClient)
            }).catch((error) => {
                if (!error.response || error.response.status === CODE_CONNECT || error.response.status === CODE_UPDATE) {
                    if (!isCheckServer) {
                        this.checkServerIsUpdating()
                            .then((response) => {
                                if (!response.isEnable) {
                                    //window.location.href = "updateserver";
                                    reject(error);
                                } else {
                                    Token.destroySession();
                                    reject(error);
                                }
                            }).catch(() => {
                                //window.location.href = "updateserver";
                                reject(error);
                            })
                    } else {
                        reject(error)
                    }
                } else {
                    Token.destroySession();
                    reject(error);
                }
            })
        })
    },

    getLocation() {
        if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(function (location) {
                const googleApiKey = process.env.VUE_APP_GOOGLE_API_KEY;
                const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.coords.latitude},${location.coords.longitude}&key=${googleApiKey}`;
                axios.get(`${url}`, {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8'
                    }
                }).then((response) => {
                    var formatted_address = response.data.results[0].formatted_address
                    Session.set('cache_address', formatted_address)
                    Session.set('hasGetLocation', true)
                    if (Session.has('Profile') && Session.get('Profile') != null && Session.get('Profile') != "") {
                        ApiManager.saveDevice("/api/Notification/SaveDevice?userId=" + Session.get('Profile').id)
                    }
                }).catch((error) => {
                    logger.error(`getLocation: ${error}`)
                });
            });
        }
    },

    saveDevice(url) {
        return new Promise((success, reject) => {

            if (Token.getTokenFCM() != undefined) {
                var paramsBody = {
                    "deviceId": ApiManager.getDeviceId(),
                    "deviceName": browser.getBrowserName(),
                    "tokenFCM": Token.getTokenFCM(),
                    "location": Session.get('cache_address'),
                    "language": document.documentElement.lang
                }
                
                ApiManager.put(url, paramsBody)
                    .then((response) => {
                        success(response)
                    }).catch((error) => {
                        reject(error)
                    })
            } else {
                success()
            }
        })

    },

    removeDevice(url) {
        return new Promise((success, reject) => {
            ApiManager.delete(url)
                .then((response) => {
                    success(response)
                }).catch((error) => {
                    reject(error)
                })
        })
    },

    checkServerIsUpdating() {
        isCheckServer = true
        return new Promise((success, reject) => {
            var url = "/api/Auth/OperatingSystem"
            axios.post(`${url}`, {
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                }
            }).then((response) => {
                isCheckServer = false
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                
                success(responseData)
            }).catch((error) => {
                isCheckServer = false
                reject(error)
            });
        })
    },
    removePayItem(payItem) {
        isCheckServer = true;
        return new Promise((success, reject) => {
            var url = "/api/Subscriptions/RemovePayItem"
            axios.post(`${url}`,payItem, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },
    addPayItems(data) {
        isCheckServer = true;
        return new Promise((success, reject) => {
            var url = "/api/Subscriptions/AddPayItems"
            axios.post(`${url}`,data, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },
    empLeaveDetail(data) {
        isCheckServer = true;
        return new Promise((success, reject) => {
            var url = "/api/Payroll/EmpLeavePayDetail"
            axios.get(`${url}?employeeId=${data.employeeId}&companyId=${data.companyId}&startPayPeriod=${data.startPayPeriod}&endPayPeriod=${data.endPayPeriod}`, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },

    /** Get the employee termination date */
    employeeTermination(data) {
        isCheckServer = true;
        return new Promise((success, reject) => {
            var url = "/api/Payroll/getEmployeeTerminationDate"
            axios.get(`${url}?employeeId=${data.employeeId}&companyId=${data.companyId}&startPayPeriod=${data.startPayPeriod}&endPayPeriod=${data.endPayPeriod}`, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },

    /**
     * Advisory call to fetch suggested termination figures for the termination dialog.
     * Returns SuggestedUnusedLslHours (0 when not payable for the state/cessation combo),
     * IsLslPayable + LslNotPayableReason, PilonNoticeWeeks + PilonHintDollars.
     */
    suggestTermination(data) {
        isCheckServer = true;
        return new Promise((success, reject) => {
            var url = "/api/Payroll/SuggestTermination"
            var query = `?employeeId=${data.employeeId}` +
                        `&companyId=${data.companyId}` +
                        `&terminationDate=${encodeURIComponent(data.terminationDate)}` +
                        `&cessationCode=${encodeURIComponent(data.cessationCode || '')}`;
            axios.get(url + query, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },
    terminationApiCall(data) {
        isCheckServer = true;
        return new Promise((success, reject) => {
            var url = "/api/Employee/EmployeeTerminationCheck"
            axios.get(`${url}?employeeId=${data.employeeId}&companyId=${data.companyId}&startPayPeriod=${data.startPayPeriod}&endPayPeriod=${data.endPayPeriod}`, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },
    async sendOTP(data) {
         return new Promise((success, reject) => {
            var url = "/api/OTPSendMail/SendOtp"
            axios.post(`${url}`,data, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },
    async verifyOTP(data) {
        // return axios.post('/api/email/verify-otp', data);
        return new Promise((success, reject) => {
            var url = "/api/OTPSendMail/verifyOtp"
            axios.post(`${url}`,data, {headers: {
                'Authorization': "Bearer " + Token.getToken(),
                        'Content-Type': 'application/json; charset=utf-8'
            }}).then((response) => {
                const responseData = JSON.parse(AES.decrypt(response.data, url));
                success(responseData)
            }).catch((error) => {
                reject(error)
            });
        })
    },
}

export default ApiManager;
